import React, { useState, useEffect } from "react";
import "./VendorDetails.css";
import { useNavigate, useParams } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import { getVendorById, updateVendor, toggleVendorStatus, verifyVendor } from "../../services/userManagement";

import LoadingSpinner from "../common/LoadingSpinner/LoadingSpinner";

const VendorDetails = () => {
  const navigate = useNavigate();
  const { id: vendorId } = useParams();
  const [vendorData, setVendorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    console.log("VendorDetails useEffect triggered with vendorId:", vendorId);
    if (vendorId && vendorId !== 'undefined') {
      fetchVendorData();
    } else {
      console.error("Invalid vendorId in useEffect:", vendorId);
      setError("Invalid vendor ID");
      setLoading(false);
    }
  }, [vendorId]);

  const fetchVendorData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("Fetching vendor data for ID:", vendorId);

      if (!vendorId || vendorId === 'undefined') {
        throw new Error("Invalid vendor ID");
      }

      const response = await getVendorById(vendorId);
      const vendorData = response.data || response;

      // Transform backend data to frontend expected format
      const transformedVendorData = {
        _id: vendorData.id,
        firstName: vendorData.firstName,
        lastName: vendorData.lastName,
        email: vendorData.email,
        phoneNumber: vendorData.phoneNumber || 'N/A',
        verified: vendorData.verified,
        isBlocked: vendorData.disabled || false,
        role: vendorData.roles && vendorData.roles.length > 0 ? vendorData.roles[0] : 'VENDOR',
        businessName: vendorData.businessName || 'N/A',
        createdAt: vendorData.createdAt || new Date().toISOString(),
      };

      setVendorData(transformedVendorData);
    } catch (error) {
      console.error("Error fetching vendor data:", error);
      setError("Failed to fetch vendor data. Please check your connection and try again.");
      setVendorData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateVendor(vendorId, vendorData);
      alert("Vendor updated successfully!");
      navigate("/managements");
    } catch (error) {
      console.error("Error updating vendor:", error);
      setError("Failed to update vendor. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    try {
      await toggleVendorStatus(vendorId, !vendorData.isBlocked);
      setVendorData(prev => ({ ...prev, isBlocked: !prev.isBlocked }));
      alert(`Vendor ${!vendorData.isBlocked ? 'blocked' : 'unblocked'} successfully!`);
    } catch (error) {
      console.error("Error toggling vendor status:", error);
      setError("Failed to update vendor status. Please try again.");
    }
  };

  const handleVerify = async () => {
    try {
      await verifyVendor(vendorId);
      setVendorData(prev => ({ ...prev, verified: true }));
      alert("Vendor verified successfully!");
    } catch (error) {
      console.error("Error verifying vendor:", error);
      setError("Failed to verify vendor. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="vendor-details-container">
        <LoadingSpinner message="Loading vendor details..." />
      </div>
    );
  }

  if (!vendorData) {
    return (
      <div className="vendor-details-container">
        <div className="error-container">
          <p>Vendor not found</p>
          <button onClick={() => navigate("/managements")}>
            Back to Managements
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="vendor-details-container">
      <div className="header-section">
        <button className="back-button" onClick={() => navigate(-1)}>
          <IoArrowBack /> Back
        </button>
        <h1 className="vendor-title">Vendor Information</h1>
        <div className="header-actions">
          {!vendorData.verified && (
            <button className="verify-button" onClick={handleVerify}>
              Verify
            </button>
          )}
          <button
            className={`status-button ${vendorData.isBlocked ? 'unblock' : 'block'}`}
            onClick={handleToggleStatus}
          >
            {vendorData.isBlocked ? 'Unblock' : 'Block'}
          </button>
          <button className="save-button" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <p style={{ fontSize: '0.9rem', color: '#6c757d', marginTop: '0.5rem' }}>
            This might be due to backend connectivity issues. Please ensure the backend server is running.
          </p>
          <button onClick={fetchVendorData} className="retry-button">
            Retry
          </button>
        </div>
      )}

      <section className="identity-section">
        <h2>Identity</h2>
        <div className="info-grid">
          <div className="info-item">
            <label>First Name:</label>
            <div className="info-value">{vendorData.firstName}</div>
          </div>
          <div className="info-item">
            <label>Last Name:</label>
            <div className="info-value">{vendorData.lastName}</div>
          </div>
        </div>
      </section>

      <section className="vendor-info-section">
        <h2>Vendor Information</h2>
        <div className="info-grid">
          <div className="info-item">
            <label>Business Name:</label>
            <div className="info-value">{vendorData.businessName}</div>
          </div>
          <div className="info-item">
            <label>Role:</label>
            <div className="info-value">{vendorData.role}</div>
          </div>
        </div>
      </section>

      <section className="proof-section">
        <h2>Proof of Commencement</h2>
        <div className="info-grid">
          <div className="info-item">
            <label>Certificate 1</label>
            <button className="certificate-view-button">View</button>
          </div>
          <div className="info-item">
            <label>Certificate 2</label>
            <button className="certificate-view-button">View</button>
          </div>
        </div>
      </section>

      <section className="contact-section">
        <h2>Contact</h2>
        <div className="info-grid">
          <div className="info-item">
            <label>Phone Number:</label>
            <div className="info-value">{vendorData.phoneNumber}</div>
          </div>
          <div className="info-item">
            <label>Email:</label>
            <div className="info-value">{vendorData.email}</div>
          </div>
        </div>
      </section>

      <section className="authentication-section">
        <h2>Account Information</h2>
        <div className="info-grid">
          <div className="info-item">
            <label>Status:</label>
            <div className={`info-value status-badge ${vendorData.isBlocked ? 'blocked' : 'active'}`}>
              {vendorData.isBlocked ? 'Blocked' : 'Active'}
            </div>
          </div>
          <div className="info-item">
            <label>Verification:</label>
            <div className={`info-value ${vendorData.verified ? "verified" : "not-verified"}`}>
              {vendorData.verified ? "Verified" : "Not Verified"}
            </div>
          </div>
        </div>
        <div className="info-grid">
          <div className="info-item">
            <label>Created:</label>
            <div className="info-value">
              {vendorData.createdAt ? new Date(vendorData.createdAt).toLocaleDateString() : 'N/A'}
            </div>
          </div>
          <div className="info-item">
            <button className="reset-password-button">Reset Password</button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VendorDetails;
